import {AggregatedAssetState} from "src/app/domain/assets/aggregated-state/aggregated-asset-state";
import {inject, Injectable} from "@angular/core";
import {DataStorageService} from "src/app/infrastructure/data-storage.service";
import {AggregatedAssetStateStore} from "src/app/domain/assets/aggregated-state/aggregated-asset-state.store";
import {map, Observable, tap} from "rxjs";
import {
  AggregatedAssetStateApiClient,
  RawAggregatedAssetState
} from "src/app/infrastructure/moovement-api/aggregated-asset-state.api-client";

@Injectable({
  providedIn: 'root'
})
export class AggregatedAssetStateService {

  private aggregatedAssetStateStore = inject(AggregatedAssetStateStore);

  constructor(
    private aggregatedAssetStateApiClient: AggregatedAssetStateApiClient,
    private dataStorageService: DataStorageService
  ) {
  }

  reloadAggregatedAssetState(assetId: string): Observable<void> {
    return this.aggregatedAssetStateApiClient.getAggregatedAssetStates([assetId]).pipe(
      tap(states => {
        if (!states || states.length === 0) return;
        states.forEach(state => this.aggregatedAssetStateStore.setEntity(this.toState(state)));
      }),
      map(_ => undefined)
    );
  }

  getAggregatedAssetStates(farmId: string, assetIds: string[]) {
    this.aggregatedAssetStateApiClient.getAggregatedAssetStates(assetIds).subscribe({
      next: async rawAggregatedAssetStates => {
        this.aggregatedAssetStateStore.setAllEntities(this.toStates(rawAggregatedAssetStates));
        await this.dataStorageService.addKeyValuePair(`aggregatedAssetStates-${farmId}`, rawAggregatedAssetStates);
      },
      error: async () => {
        const cachedStates = await this.dataStorageService.getValueByKey<RawAggregatedAssetState[]>(`aggregatedAssetStates-${farmId}`);
        if (!cachedStates) return;
        this.aggregatedAssetStateStore.setAllEntities(this.toStates(cachedStates));
      }
    });
  }

  private toStates(rawStates: RawAggregatedAssetState[]): AggregatedAssetState[] {
    return rawStates.map(rawState => this.toState(rawState));
  }

  private toState(rawState: RawAggregatedAssetState): AggregatedAssetState {
    return {
      id: rawState.id,
      outOfFarmSince: this.toDate(rawState.outOfFarmSince),
      outOfPaddockSince: this.toDate(rawState.outOfPaddockSince),
      stoppedMovingSince: this.toDate(rawState.stoppedMovingSince),
      abnormalActivitySince: this.toDate(rawState.abnormalActivitySince),
      abnormalActivityStatus: rawState.abnormalActivityStatus || undefined,
      lastSeenByGatewayAt: this.toDate(rawState.lastSeenByGatewayAt),
      lastSeenByBLEScannerAt: this.toDate(rawState.lastSeenByBLEScannerAt),
      lastGPSFixAt: this.toDate(rawState.lastGPSFixAt),
      lastNfcGpsFixAt: this.toDate(rawState.lastNfcGpsFixAt),
      position: rawState.position,
      lastBatteryVoltageAt: this.toDate(rawState.lastBatteryVoltageAt),
      batteryVoltage: rawState.batteryVoltage,
      lastObservingDevice: rawState.lastObservingDevice,
      pregnancyChance: rawState.pregnancyChance,
      parents: rawState.parents,
      children: rawState.children,
    };
  }

  private toDate(value?: string) {
    if (!value) return undefined;

    return new Date(value);
  }
}
